import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'

const LoginPage = () => {
  const { t } = useTranslation('auth')
  const [showPassword, setShowPassword] = useState(false)
  const { login, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue
  } = useForm()

  const onSubmit = async (data) => {
    const result = await login(data)

    if (result.success) {
      navigate(from, { replace: true })
    } else {
      setError('root', { message: result.error })
    }
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-bg)' }}>
      {/* Left side — illustration */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-8" style={{ background: 'var(--color-surface)' }}>
        <img
          src="/auth-illustration.png"
          alt=""
          className="w-full object-contain px-4"
          style={{ maxHeight: '85vh' }}
        />
      </div>

      {/* Right side — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1f9f93] p-2" style={{ boxShadow: '0 10px 22px rgba(31,159,147,.22)' }}>
                <img src="/logo.png" alt="Finomi" className="w-10 h-10 object-contain brightness-0 invert" />
              </div>
            </div>
            <h2 className="mt-6 text-[28px] font-black tracking-[.2px]" style={{ color: 'var(--color-text)' }}>
              {t('login.title')}
            </h2>
            <p className="mt-2 text-[13px] font-semibold" style={{ color: 'var(--color-text-muted)' }}>
              {t('login.subtitle')}
            </p>
          </div>

          {/* Success message after password reset */}
          {location.state?.passwordReset && (
            <div className="rounded-xl p-3" style={{ background: 'rgba(31,159,147,.10)', border: '1px solid rgba(31,159,147,.18)' }}>
              <p className="text-sm font-bold text-center" style={{ color: '#1f9f93' }}>
                {t('login.passwordResetSuccess')}
              </p>
            </div>
          )}

          {/* Login form */}
          <Card>
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              {/* Email */}
              <div>
                <label htmlFor="email" className="form-label">
                  {t('login.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="off"
                  className="form-input mt-1"
                  placeholder={t('login.emailPlaceholder')}
                  {...register('email', {
                    required: t('validation:emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('validation:emailInvalid')
                    }
                  })}
                />
                {errors.email && (
                  <p className="form-error mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="form-label">
                  {t('login.passwordLabel')}
                </label>
                <div className="relative mt-1">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="off"
                    className="form-input pr-10"
                    placeholder={t('login.passwordPlaceholder')}
                    {...register('password', {
                      required: t('validation:passwordRequired')
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" style={{ color: 'var(--color-text-faint)' }} />
                    ) : (
                      <EyeIcon className="h-5 w-5" style={{ color: 'var(--color-text-faint)' }} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="form-error mt-1">{errors.password.message}</p>
                )}
              </div>

              {/* Remember me & Forgot password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    style={{ accentColor: '#1f9f93' }}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
                    {t('login.rememberMe')}
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-bold no-underline"
                    style={{ color: '#1f9f93' }}
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </div>

              {/* Error message */}
              {errors.root && (
                <div className="rounded-xl p-3" style={{ background: 'rgba(229,83,83,.10)', border: '1px solid rgba(229,83,83,.18)' }}>
                  <p className="text-sm font-bold" style={{ color: '#e55353' }}>{errors.root.message}</p>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                className="w-full"
              >
                {t('login.submit')}
              </Button>
            </form>
          </Card>

          {/* Register link */}
          <div className="text-center">
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-muted)' }}>
              {t('login.noAccount')}{' '}
              <Link
                to="/register"
                className="font-bold no-underline"
                style={{ color: '#1f9f93' }}
              >
                {t('login.register')}
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  )
}

export default LoginPage
